/**
 * /dashboard/posh/training/$moduleId/quiz — Quiz runner for a training
 * module.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/posh/training/[moduleId]/quiz/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { localize, toApiError } from "@/lib/api";
import { useTrainingModules } from "@/hooks/use-posh";
import { PageHeader } from "@/components/shared/page-header";
import { PoshFeatureGate } from "@/components/posh/posh-feature-gate";
import { QuizRunner } from "@/components/posh/quiz-runner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/posh/training/$moduleId/quiz",
)({
  component: QuizRoute,
});

function QuizBody({ moduleId }: { moduleId: number }) {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const modulesQ = useTrainingModules();

  if (modulesQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-1/2" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  if (modulesQ.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load this module</AlertTitle>
        <AlertDescription>
          {toApiError(modulesQ.error).message}
        </AlertDescription>
      </Alert>
    );
  }

  const entry = modulesQ.data?.modules.find(
    (m) => m.module.id === moduleId,
  );

  if (!entry) {
    return (
      <Alert>
        <AlertTitle>Module not found</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>This module does not exist or is no longer available.</p>
          <Button asChild variant="outline" size="sm">
            <Link to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}>Back to training</Link>
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const { module } = entry;

  return (
    <>
      <div className="mb-4">
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1.5 text-muted-foreground"
        >
          <Link
            to="/app/$tenantSlug/posh/training/$moduleId"
            params={{tenantSlug, moduleId: String(module.id)}}
          >
            <ArrowLeft className="h-4 w-4" />
            {localize(module.title) || module.code}
          </Link>
        </Button>
      </div>
      <PageHeader
        title="Module Quiz"
        description={`Pass with ${module.passing_score}% or higher to earn your certificate.`}
      />
      <QuizRunner module={module} />
    </>
  );
}

function QuizRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { moduleId } = Route.useParams();
  const id = Number(moduleId);

  return (
    <PoshFeatureGate>
      {Number.isFinite(id) && id > 0 ? (
        <QuizBody moduleId={id} />
      ) : (
        <Alert variant="destructive">
          <AlertTitle>Invalid module</AlertTitle>
          <AlertDescription>
            That quiz link is not valid.{" "}
            <Link
              className="font-medium text-primary underline"
              to="/app/$tenantSlug/posh/training" params={{ tenantSlug }}
            >
              Return to training
            </Link>
            .
          </AlertDescription>
        </Alert>
      )}
    </PoshFeatureGate>
  );
}
